import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Stack,
  Text,
  Button,
  Group,
  Alert,
  Card,
  Code,
  Collapse,
  ActionIcon
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconRefresh,
  IconBug,
  IconChevronDown,
  IconChevronUp
} from '@tabler/icons-react';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

/**
 * Planner Error Boundary Component
 * 
 * Features from UX Flow Spec:
 * - Catches JavaScript errors anywhere in the component tree
 * - Displays fallback UI instead of crashing the entire app
 * - Provides detailed error information for debugging
 * - Allows users to retry or refresh the application
 * - Logs errors for monitoring and debugging
 * - Graceful degradation for production environments
 */
class PlannerErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console in development
    if (import.meta.env['MODE'] === 'development') {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to external error reporting service in production
    if (import.meta.env['MODE'] === 'production') {
      // TODO: Integrate with error reporting service (e.g., Sentry)
      this.logErrorToService(error, errorInfo);
    }
  }

  private logErrorToService = (error: Error, _errorInfo: ErrorInfo) => {
    try {
      // Example integration with error reporting service
      // Sentry.captureException(error, {
      //   contexts: {
      //     react: {
      //       componentStack: errorInfo.componentStack
      //     }
      //   }
      // });
      console.error('Error logged to service:', error);
    } catch (loggingError) {
      console.error('Failed to log error to service:', loggingError);
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    });
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box p="xl" maw={600} mx="auto">
          <Stack gap="md">
            {/* Error Header */}
            <Alert
              icon={<IconAlertTriangle size={20} />}
              title="Application Error"
              color="red"
              variant="light"
            >
              <Text size="sm">
                Something went wrong while loading the planner. This has been reported to our team.
              </Text>
            </Alert>

            {/* Error Card */}
            <Card withBorder p="md" radius="md">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <Box style={{ flex: 1 }}>
                    <Text fw={600} size="lg" c="red">
                      Planner Error
                    </Text>
                    <Text size="sm" c="dimmed" mt="xs">
                      The room scheduler encountered an unexpected error and couldn't continue.
                    </Text>
                  </Box>
                  <ActionIcon
                    variant="light"
                    onClick={this.toggleDetails}
                    size="sm"
                  >
                    {this.state.showDetails ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                  </ActionIcon>
                </Group>

                {/* Error Message */}
                {this.state.error && (
                  <Box>
                    <Text size="sm" fw={500} mb="xs">Error Message:</Text>
                    <Code block color="red">
                      {this.state.error.message}
                    </Code>
                  </Box>
                )}

                {/* Error Details (Collapsible) */}
                <Collapse in={this.state.showDetails}>
                  <Stack gap="md">
                    {this.state.error && (
                      <Box>
                        <Text size="sm" fw={500} mb="xs">Error Stack:</Text>
                        <Code block color="gray" style={{ fontSize: '12px' }}>
                          {this.state.error.stack}
                        </Code>
                      </Box>
                    )}

                    {this.state.errorInfo && (
                      <Box>
                        <Text size="sm" fw={500} mb="xs">Component Stack:</Text>
                        <Code block color="gray" style={{ fontSize: '12px' }}>
                          {this.state.errorInfo.componentStack}
                        </Code>
                      </Box>
                    )}
                  </Stack>
                </Collapse>

                {/* Action Buttons */}
                <Group gap="sm" justify="flex-end">
                  <Button
                    variant="light"
                    leftSection={<IconBug size={16} />}
                    onClick={this.toggleDetails}
                    size="sm"
                  >
                    {this.state.showDetails ? 'Hide Details' : 'Show Details'}
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconRefresh size={16} />}
                    onClick={this.handleRetry}
                    size="sm"
                  >
                    Try Again
                  </Button>
                  <Button
                    leftSection={<IconRefresh size={16} />}
                    onClick={this.handleRefresh}
                    size="sm"
                  >
                    Refresh Page
                  </Button>
                </Group>
              </Stack>
            </Card>

            {/* Help Text */}
            <Card withBorder p="md" radius="md" bg="gray.0">
              <Stack gap="sm">
                <Text fw={500} size="sm">What you can do:</Text>
                <Stack gap="xs">
                  <Text size="sm">• Try refreshing the page</Text>
                  <Text size="sm">• Check your internet connection</Text>
                  <Text size="sm">• Clear your browser cache</Text>
                  <Text size="sm">• Contact support if the problem persists</Text>
                </Stack>
              </Stack>
            </Card>

            {/* Development Info */}
            {import.meta.env['MODE'] === 'development' && this.state.error && (
              <Card withBorder p="md" radius="md" bg="yellow.0">
                <Stack gap="sm">
                  <Text fw={500} size="sm" c="orange">Development Mode</Text>
                  <Text size="sm">
                    Error details are shown above. Check the browser console for more information.
                  </Text>
                </Stack>
              </Card>
            )}
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

// =============================================================================
// SPECIFIC ERROR BOUNDARIES
// =============================================================================

/**
 * Grid Error Boundary - Specific error handling for planner grid
 */
export const GridErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <PlannerErrorBoundary
      fallback={
        <Box p="xl" ta="center">
          <Stack gap="md" align="center">
            <IconAlertTriangle size={48} color="var(--mantine-color-red-6)" />
            <Text fw={600} size="lg">Grid Loading Error</Text>
            <Text size="sm" c="dimmed" maw={400}>
              Unable to load the planner grid. Please try refreshing the page or contact support.
            </Text>
            <Group gap="sm">
              <Button variant="light" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            </Group>
          </Stack>
        </Box>
      }
    >
      {children}
    </PlannerErrorBoundary>
  );
};

/**
 * Floor Plan Error Boundary - Specific error handling for floor plan canvas
 */
export const FloorPlanErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <PlannerErrorBoundary
      fallback={
        <Box p="xl" ta="center">
          <Stack gap="md" align="center">
            <IconAlertTriangle size={48} color="var(--mantine-color-red-6)" />
            <Text fw={600} size="lg">Floor Plan Error</Text>
            <Text size="sm" c="dimmed" maw={400}>
              Unable to load the floor plan visualization. Please try switching to grid view or refresh the page.
            </Text>
            <Group gap="sm">
              <Button variant="light" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            </Group>
          </Stack>
        </Box>
      }
    >
      {children}
    </PlannerErrorBoundary>
  );
};

/**
 * Modal Error Boundary - Specific error handling for modals
 */
export const ModalErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <PlannerErrorBoundary
      fallback={
        <Box p="md">
          <Stack gap="md" align="center">
            <IconAlertTriangle size={32} color="var(--mantine-color-red-6)" />
            <Text fw={600}>Modal Error</Text>
            <Text size="sm" c="dimmed" ta="center">
              This modal encountered an error. Please close it and try again.
            </Text>
            <Button size="sm" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Stack>
        </Box>
      }
    >
      {children}
    </PlannerErrorBoundary>
  );
};

export default PlannerErrorBoundary;
